import { Link } from "react-router-dom";
import type { FCAData } from "@/types/fca";
import { UTM_LABEL } from "@/lib/utm";
import LotRelationCard from "./LotRelationCard";

interface Props {
  data: FCAData;
}

export default function TreeLocationCard({ data }: Props) {
  const embedUrl = `https://maps.google.com/maps?q=${data.latitude},${data.longitude}&z=18&output=embed`;

  const copyCoords = () => {
    navigator.clipboard
      .writeText(`${data.latitude}, ${data.longitude} (EPSG:4326)
E ${data.utmE} N ${data.utmN} (EPSG:31982)`)
      .catch(() => {});
  };

  return (
    <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Location details */}
      <div className="bg-white rounded-card border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-4 flex items-center gap-2">
          <i className="ri-map-pin-line text-accent-500"></i>
          Dados de Localização
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Município" value={data.municipio} />
          <Field label="UF" value={data.uf} />
          <Field label="Bairro" value={data.bairro} />
          <Field label="Logradouro" value={data.logradouro} />
          <Field label="Número / Referência" value={data.numero} />
          <Field label="Sistema geográfico" value={data.srid} mono />
          <Field label="Latitude" value={data.latitude} mono />
          <Field label="Longitude" value={data.longitude} mono />
        </div>
        <div className="mt-4 pt-4 border-t border-background-200/70">
          <h4 className="text-xs font-semibold text-foreground-700 mb-2">{UTM_LABEL}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <Field label="E (leste)" value={data.utmE === "—" ? "—" : `${data.utmE} m`} mono />
            <Field label="N (norte)" value={data.utmN === "—" ? "—" : `${data.utmN} m`} mono />
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-background-200/70">
          <h4 className="text-xs font-semibold text-foreground-700 mb-2">Projeto Vinculado</h4>
          <Link to={data.projectId ? `/projetos/${data.projectId}` : "/projetos"} className="text-sm text-accent-500 hover:text-accent-600 font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-folder-chart-line"></i>
            {data.projetoVinculado}
          </Link>
        </div>
        {data.areaVerdeVinculada && (
          <div className="mt-2 pt-2 border-t border-background-200/70">
            <h4 className="text-xs font-semibold text-foreground-700 mb-2">Área Verde / UC Vinculada</h4>
            <span className="text-sm text-primary-600 font-medium flex items-center gap-1.5">
              <i className="ri-landscape-line"></i>
              {data.areaVerdeVinculada}
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-background-200/70">
          <button onClick={copyCoords} className="btn-outline text-xs whitespace-nowrap">
            <i className="ri-file-copy-line"></i>
            Copiar coordenadas
          </button>
          <a
            href={`https://www.google.com/maps?q=${data.latitude},${data.longitude}`}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="btn-primary text-xs whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i>
            Abrir mapa completo
          </a>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <iframe
          src={embedUrl}
          title={`Mapa de localização — ${data.codigoCta}`}
          className="w-full h-full min-h-[320px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        ></iframe>
      </div>
    </div>
    {Number(data.id) > 0 && <div id="fca-lot-relation" className="scroll-mt-4"><LotRelationCard treeId={Number(data.id)} /></div>}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[11px] text-foreground-500 uppercase tracking-wide">{label}</p>
      <p className={`text-sm text-foreground-900 font-medium mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}